import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar  from "./Topbar";
import { getReports } from "../api/adminApi";
import "./admin-dashboard.css";

// const BASE_URL = "https://suvidha-backend-353j.onrender.com";
const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://api.suvidhaeldercare.com";


function authHeaders() {
  const t = localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
}

const TYPE_DESC = {
  Users:         "All registered users with join date, phone, survey status",
  Subscriptions: "All subscription records with plan, amount, dates, status",
  Revenue:       "Combined subscription + booking revenue with breakdown",
  Bookings:      "All service bookings with status, slot, amount, payment",
  Feedback:      "All customer reviews with ratings and comments",
  Expiry:        "Active subscriptions expiring within 30 days",
};

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // type being downloaded

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getReports();
        if (!alive) return;
        setReports(data);
      } catch (err) {
        alert(err?.response?.data?.message || "Failed to load reports");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, []);

  const handleDownload = async (type) => {
    setDownloading(type);
    try {
      const res = await fetch(
        `${BASE_URL}/api/admin/reports/${type}/download`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Download failed");
      }
      // Get filename from Content-Disposition header
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="(.+?)"/);
      const filename = match ? match[1] : `${type.toLowerCase()}_report.csv`;

      // Trigger browser download
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  const today = new Date().toLocaleDateString("en-IN", {
    day: "numeric", month: "long", year: "numeric"
  });

  return (
    <div className="adm-layout">
      <Sidebar />
      <main className="adm-main">
        <div className="adm-shell">
          <Topbar title="Reports" subtitle="Download real-time data as CSV" />

          {/* Info banner */}
          <div style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE",
            borderRadius: 14, padding: "14px 18px",
            marginBottom: 24, display: "flex", gap: 12, alignItems: "center"
          }}>
            <span style={{ fontSize: 22 }}>📊</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1E40AF" }}>
                Live Data Reports — {today}
              </div>
              <div style={{ fontSize: 13, color: "#3B82F6", marginTop: 2 }}>
                All reports are generated in real-time from the database. Download as CSV — opens directly in Excel.
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
              Loading reports...
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 16
            }}>
              {reports.map(r => {
                const isLoading = downloading === r.type;
                return (
                  <div key={r.id} style={{
                    background: "#fff",
                    border: "1px solid #E2E8F0",
                    borderRadius: 18,
                    padding: "22px 24px",
                    borderLeft: `5px solid ${r.color}`,
                    display: "flex", flexDirection: "column", gap: 14,
                    boxShadow: "0 2px 12px rgba(0,0,0,.05)"
                  }}>
                    {/* Top row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{
                        width: 46, height: 46, borderRadius: 12,
                        background: r.bg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 22, flexShrink: 0
                      }}>
                        {r.icon}
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "3px 10px",
                        borderRadius: 999, background: r.bg, color: r.color
                      }}>{r.type}</span>
                    </div>

                    {/* Title + summary */}
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B", marginBottom: 4 }}>
                        {r.title}
                      </div>
                      <div style={{ fontSize: 13, color: "#64748B", fontWeight: 600 }}>
                        {r.summary}
                      </div>
                      <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6, lineHeight: 1.5 }}>
                        {TYPE_DESC[r.type]}
                      </div>
                    </div>

                    {/* Footer */}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      alignItems: "center", paddingTop: 12,
                      borderTop: "1px solid #F1F5F9"
                    }}>
                      <div style={{ fontSize: 12, color: "#94A3B8" }}>
                        📅 Generated: {r.date}
                      </div>
                      <button
                        onClick={() => handleDownload(r.type)}
                        disabled={isLoading}
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "8px 16px", borderRadius: 10, border: "none",
                          background: isLoading ? "#93C5FD" : r.color,
                          color: "#fff", cursor: isLoading ? "not-allowed" : "pointer",
                          fontWeight: 700, fontSize: 13, fontFamily: "inherit",
                          transition: ".2s"
                        }}
                      >
                        {isLoading ? (
                          <>
                            <span style={{
                              width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)",
                              borderTopColor: "#fff", borderRadius: "50%",
                              display: "inline-block",
                              animation: "spin .7s linear infinite"
                            }} />
                            Generating...
                          </>
                        ) : (
                          <>⬇️ Download CSV</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </main>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}